import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Save } from "lucide-react";
import { fromKm, toKm, fmt, unitLabel } from "@/lib/units";

export default function VehicleManageDialog({
  open,
  onOpenChange,
  vehicles,
  unit,
  onCreate,
  onUpdate,
  onDelete,
}) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [newLimit, setNewLimit] = useState("");
  const [newColor, setNewColor] = useState("#FFFFFF");

  useEffect(() => {
    if (open) {
      setItems(
        vehicles.map((v) => ({
          id: v.id,
          name: v.name,
          color: v.color || "#FFFFFF",
          limit: fromKm(v.yearly_limit_km || 0, unit).toFixed(0),
          image_url: v.image_url || "",
        }))
      );
      setNewName("");
      setNewLimit("");
      setNewColor("#FFFFFF");
    }
  }, [open, vehicles, unit]);

  const saveOne = async (it) => {
    await onUpdate(it.id, {
      name: it.name,
      color: it.color,
      yearly_limit_km: toKm(parseFloat(it.limit) || 0, unit),
    });
  };

  const addNew = async () => {
    if (!newName || !newLimit) return;
    await onCreate({
      name: newName,
      yearly_limit_km: toKm(parseFloat(newLimit), unit),
      color: newColor,
    });
    setNewName("");
    setNewLimit("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[640px] max-h-[80vh] overflow-y-auto"
        data-testid="vehicle-manage-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">
            Manage Vehicles
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            Configure yearly mileage limits ({unitLabel(unit)}) for each vehicle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className="bg-[var(--bg-input)] border border-[var(--border-subtle)] rounded-md p-4 space-y-3"
              data-testid={`vehicle-row-${it.id}`}
            >
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5 space-y-1">
                  <Label className="label-tiny">Name</Label>
                  <Input
                    value={it.name}
                    onChange={(e) => {
                      const arr = [...items];
                      arr[idx].name = e.target.value;
                      setItems(arr);
                    }}
                    className="bg-[var(--bg-deep)] border-[var(--border-subtle)] text-[var(--text-primary)]"
                    data-testid={`vehicle-name-${it.id}`}
                  />
                </div>
                <div className="col-span-4 space-y-1">
                  <Label className="label-tiny">Yearly Limit ({unitLabel(unit)})</Label>
                  <Input
                    type="number"
                    value={it.limit}
                    onChange={(e) => {
                      const arr = [...items];
                      arr[idx].limit = e.target.value;
                      setItems(arr);
                    }}
                    className="bg-[var(--bg-deep)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                    data-testid={`vehicle-limit-${it.id}`}
                  />
                </div>
                <div className="col-span-1 space-y-1">
                  <Label className="label-tiny">Color</Label>
                  <input
                    type="color"
                    value={it.color}
                    onChange={(e) => {
                      const arr = [...items];
                      arr[idx].color = e.target.value;
                      setItems(arr);
                    }}
                    className="w-full h-9 bg-[var(--bg-deep)] border border-[var(--border-subtle)] rounded cursor-pointer"
                    data-testid={`vehicle-color-${it.id}`}
                  />
                </div>
                <div className="col-span-2 flex gap-1">
                  <Button
                    size="icon"
                    onClick={() => saveOne(it)}
                    className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)] flex-1 h-9"
                    data-testid={`vehicle-save-${it.id}`}
                  >
                    <Save className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => onDelete(it.id)}
                    className="bg-transparent border-[var(--border-subtle)] text-[#FF6961] hover:bg-[#FF3B30]/10 hover:text-[#FF6961] h-9"
                    data-testid={`vehicle-delete-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {/* Add new */}
          <div className="bg-[var(--bg-input)] border border-dashed border-[var(--border-subtle)] rounded-md p-4">
            <div className="label-tiny mb-3">Add Vehicle</div>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                <Input
                  placeholder="e.g., Model 3"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="bg-[var(--bg-deep)] border-[var(--border-subtle)] text-[var(--text-primary)]"
                  data-testid="new-vehicle-name"
                />
              </div>
              <div className="col-span-4">
                <Input
                  type="number"
                  placeholder={`Limit ${unitLabel(unit)}`}
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  className="bg-[var(--bg-deep)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                  data-testid="new-vehicle-limit"
                />
              </div>
              <div className="col-span-1">
                <input
                  type="color"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="w-full h-9 bg-[var(--bg-deep)] border border-[var(--border-subtle)] rounded cursor-pointer"
                  data-testid="new-vehicle-color"
                />
              </div>
              <div className="col-span-2">
                <Button
                  onClick={addNew}
                  disabled={!newName || !newLimit}
                  className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)] w-full h-9"
                  data-testid="new-vehicle-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={2} /> Add
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            data-testid="vehicle-manage-close-btn"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
